// Plan-BundlesImplementation §E.1 — Text Bundle allocation modal.
//
// Allows the workspace owner/admin to split a $5.00 Text Bundle across
// five text resources at v3 per-resource pricing. Internal math is in
// "tenth-mill" units (1 unit = $0.0001 = 0.01 cents) per
// `front/src/types/billing.ts:TEXT_BUNDLE_RATE_UNITS` to avoid float
// drift in the sum-to-$5.00 validation (plan §F risk #3).
//
// On Purchase: hits `billingAPI.buyTextBundle(allocation)`, then
// `window.location.href = checkout_url` to redirect to Stripe Checkout.
// On return, the existing post-checkout `session_id` poll in
// BillingPage.tsx covers state refresh.

import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X, CreditCard, AlertTriangle, Info } from 'lucide-react';
import {
  TEXT_BUNDLE_RATE_UNITS,
  TEXT_BUNDLE_RESOURCES,
  TEXT_BUNDLE_TOTAL_UNITS,
  TEXT_BUNDLE_DISPLAY,
  type TextBundleResource,
} from '../../types/billing';
import { billingAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface Props {
  workspaceId: string;
  onClose: () => void;
}

const formatUSD = (units: number): string => {
  // units = tenth-mills ($0.0001). Convert to dollars with up to 4
  // decimals trimmed to 2 for display when possible.
  const dollars = units / 10000;
  return `$${dollars.toFixed(2)}`;
};

const TextBundleModal: React.FC<Props> = ({ workspaceId, onClose }) => {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [allocation, setAllocation] = useState<Record<TextBundleResource, number>>({
    chat_conversations: 0,
    email_threads: 0,
    knowledge_summaries: 0,
    web_scrapes: 0,
    reports: 0,
  });

  const lineTotals = useMemo(() => {
    const totals: Record<TextBundleResource, number> = {
      chat_conversations: 0,
      email_threads: 0,
      knowledge_summaries: 0,
      web_scrapes: 0,
      reports: 0,
    };
    for (const r of TEXT_BUNDLE_RESOURCES) {
      totals[r] = allocation[r] * TEXT_BUNDLE_RATE_UNITS[r];
    }
    return totals;
  }, [allocation]);

  const grandTotalUnits = useMemo(
    () => TEXT_BUNDLE_RESOURCES.reduce((sum, r) => sum + lineTotals[r], 0),
    [lineTotals]
  );

  const remainingUnits = TEXT_BUNDLE_TOTAL_UNITS - grandTotalUnits;
  const isOverBudget = grandTotalUnits > TEXT_BUNDLE_TOTAL_UNITS;
  const hasAnyAllocation = TEXT_BUNDLE_RESOURCES.some((r) => allocation[r] > 0);
  // Purchase gate (matches backend spec): allow any non-empty allocation
  // that does not exceed the $5.00 budget. Partial allocations are valid
  // — the Text Bundle is a flat $5.00 charge regardless of how much of
  // the budget is used.
  const canPurchase = hasAnyAllocation && !isOverBudget;

  const setCount = (resource: TextBundleResource, value: string) => {
    // Empty string → 0; non-numeric → keep previous.
    if (value === '') {
      setAllocation((prev) => ({ ...prev, [resource]: 0 }));
      return;
    }
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return;
    setAllocation((prev) => ({ ...prev, [resource]: Math.max(0, n) }));
  };

  // Fill a single category with the largest integer count that fits the
  // remaining $5.00 budget, holding every other row's count fixed.
  // floor() guarantees the grand total can never exceed $5.00, and we
  // clamp to the per-category cap (and to 0 if other rows already exceed
  // the budget). Reuses the existing lineTotals / grandTotalUnits memos.
  const fillMax = (resource: TextBundleResource) => {
    if (submitting) return;
    const otherUnits = grandTotalUnits - lineTotals[resource];
    const availableUnits = TEXT_BUNDLE_TOTAL_UNITS - otherUnits;
    const maxCount = Math.max(
      0,
      Math.min(
        TEXT_BUNDLE_DISPLAY[resource].max,
        Math.floor(availableUnits / TEXT_BUNDLE_RATE_UNITS[resource])
      )
    );
    setAllocation((prev) => ({ ...prev, [resource]: maxCount }));
  };

  const handlePurchase = async () => {
    if (!canPurchase || submitting) return;
    setSubmitting(true);
    try {
      // Strip zero-allocation resources to keep the payload tight; backend
      // validation per plan §C.4 accepts a partial allocation.
      const nonZero: Partial<Record<TextBundleResource, number>> = {};
      for (const r of TEXT_BUNDLE_RESOURCES) {
        if (allocation[r] > 0) nonZero[r] = allocation[r];
      }
      const res = await billingAPI.buyTextBundle({
        workspace_id: workspaceId,
        allocation: nonZero,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      showToast('Checkout session created — redirecting…', 'success');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      // 404 = Storm's Phase 2 not deployed yet. Surface clearly so we
      // don't confuse the user with a generic error.
      if (err?.response?.status === 404) {
        showToast('Bundle purchases are not yet available. Coming soon.', 'error');
      } else if (typeof detail === 'string') {
        showToast(detail, 'error');
      } else {
        showToast('Could not create Stripe Checkout session', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Text Bundle — $5.00</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Split your $5.00 across any combination of text resources.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Allocation rows */}
          <div className="space-y-3">
            {TEXT_BUNDLE_RESOURCES.map((r) => {
              const display = TEXT_BUNDLE_DISPLAY[r];
              const subtotal = lineTotals[r];
              return (
                <div
                  key={r}
                  className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_120px_auto] gap-2 sm:gap-3 items-center"
                >
                  <div className="min-w-0">
                    <label
                      htmlFor={`alloc-${r}`}
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 block"
                    >
                      {display.label}
                    </label>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {display.perUnitLabel} each · up to {display.max.toLocaleString()}
                    </span>
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex flex-col gap-1 min-w-0">
                    <input
                      id={`alloc-${r}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={display.max}
                      value={allocation[r] === 0 ? '' : allocation[r]}
                      onChange={(e) => setCount(r, e.target.value)}
                      disabled={submitting}
                      placeholder="0"
                      className="input-field w-full text-right tabular-nums disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => fillMax(r)}
                      disabled={submitting}
                      aria-label={`Max ${display.label}`}
                      title={`Fill ${display.label} with the largest amount that fits the remaining $5.00`}
                      className="self-end px-2 py-0.5 rounded-md border border-gray-200 dark:border-gray-700 text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Max
                    </button>
                  </div>
                  <span className="text-xs sm:text-sm font-mono text-gray-600 dark:text-gray-400 tabular-nums text-right w-16 hidden sm:inline-block">
                    {formatUSD(subtotal)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Total bar */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total</span>
              <span
                className={`text-lg font-bold tabular-nums ${
                  isOverBudget
                    ? 'text-red-600 dark:text-red-400'
                    : canPurchase
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {formatUSD(grandTotalUnits)} / $5.00
              </span>
            </div>

            <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full transition-all ${
                  isOverBudget
                    ? 'bg-red-500'
                    : canPurchase
                    ? 'bg-emerald-500'
                    : 'bg-primary'
                }`}
                style={{
                  width: `${Math.min(100, (grandTotalUnits / TEXT_BUNDLE_TOTAL_UNITS) * 100)}%`,
                }}
              />
            </div>

            <div className="text-xs mt-2 space-y-1">
              {isOverBudget ? (
                <p className="text-red-600 dark:text-red-400 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Over by {formatUSD(-remainingUnits)}. Reduce one or more counts.
                </p>
              ) : canPurchase ? (
                <>
                  <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                    ✓ Ready to purchase — {formatUSD(grandTotalUnits)} of $5.00
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    The Text Bundle is a flat $5.00 charge — any unused balance
                    is not refunded.
                  </p>
                </>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Allocate at least one resource to continue.
                </p>
              )}
            </div>
          </div>

          {/* Fine print */}
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
            Credits expire 12 months from purchase. Credits are workspace-bound and
            consumed oldest-expiring-first. No refunds — see our billing policy.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handlePurchase}
            disabled={!canPurchase || submitting}
            className="px-4 py-2 rounded-lg text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-purple-600 hover:shadow-lg"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CreditCard className="w-4 h-4" />
            )}
            Purchase — $5.00
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TextBundleModal;
